import React from 'react';
import { Link } from 'react-router-dom';
import { Scale, ArrowLeft } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { LegalLanguageBanner } from '../components/LegalLanguageBanner';

const Section = ({ title, children }) => (
  <section className="bg-[#1a1a1a] border border-[#C4A55A]/20 rounded-2xl p-6 md:p-8 mb-6">
    <h2 className="text-2xl font-semibold mb-4">{title}</h2>
    <div className="text-gray-300 space-y-3 leading-relaxed">{children}</div>
  </section>
);

export const AGB = () => {
  return (
    <div className="bg-black min-h-screen text-white">
      <Header />
      <main className="container mx-auto px-4 pt-32 pb-16 max-w-4xl" data-testid="page-agb">
        <Link
          to="/"
          className="inline-flex items-center text-[#C4A55A] hover:text-[#D4B56A] mb-6 text-sm"
          data-testid="agb-back-link"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück zur Startseite
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <Scale className="h-10 w-10 text-[#C4A55A]" />
          <h1 className="text-4xl md:text-5xl font-bold">Allgemeine Geschäftsbedingungen</h1>
        </div>
        <p className="text-gray-400 mb-10">
          AGB der Löwenherz Invest AG für die Nutzung des Investoren-Portals und die Zeichnung
          von Aktien der Löwenherz Invest AG.
        </p>

        <LegalLanguageBanner />

        <Section title="1. Geltungsbereich">
          <p>
            Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für die Nutzung des Online-Portals
            der Löwenherz Invest AG sowie für die Zeichnung von Aktien der Löwenherz Invest AG
            (nachfolgend „Gesellschaft"). Mit der Registrierung als Investor oder
            Vermittler bestätigt der Nutzer, diese AGB gelesen, verstanden und akzeptiert zu haben.
          </p>
        </Section>

        <Section title="2. Anbieter">
          <p>
            Löwenherz Invest AG, Wildenstrasse 3, 8212 Neuhausen am Rheinfall, Schweiz,
            eingetragen im Handelsregister Schaffhausen unter CHE-431.558.665, vertreten durch
            Matthias Kirchner (Verwaltungsrat).
          </p>
        </Section>

        <Section title="3. Aktienzeichnung">
          <p>
            Gegenstand einer Zeichnung sind Aktien der Löwenherz Invest AG mit einem Nennwert von
            CHF 100. Die Gesellschaft plant die Ausgabe von zwei Aktien­kategorien:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Namen­aktien</strong> – mit voller Stimm- und Mitwirkungs­berechtigung an der Generalversammlung.</li>
            <li><strong>Vorzugs­aktien</strong> (geplant) – mit bevorzugter Dividenden­auszahlung gemäss Statuten, ggf. mit eingeschränktem Stimmrecht.</li>
          </ul>
          <p>
            Die konkret zur Zeichnung verfügbare Aktien­kategorie wird im Investoren-Portal jeweils
            ausgewiesen. Die Zeichnung erfolgt online über das Investoren-Portal und ist erst dann
            verbindlich, wenn:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>der Zeichnungsbetrag fristgerecht auf dem im PDF angegebenen Bankkonto der Gesellschaft eingegangen ist,</li>
            <li>der Verwaltungsrat die Zeichnung bestätigt hat, und</li>
            <li>die Eintragung im offiziellen Schweizer Aktienregister (das-aktienregister.ch) erfolgt ist.</li>
          </ul>
          <p>
            Bis zum Zeitpunkt der Bestätigung gilt die Zeichnung als unverbindliche Reservation
            („In Prüfung"). Die Gesellschaft behält sich vor, Zeichnungen ohne Angabe von Gründen
            abzulehnen.
          </p>
        </Section>

        <Section title="4. Zahlungsbedingungen">
          <p>
            Der Zeichnungsbetrag ist innert <strong>14 Tagen</strong> nach Eingang der Zeichnung
            per Banküberweisung auf das im PDF aufgeführte Bankkonto zu überweisen. Bei Nicht-Eingang
            innerhalb dieser Frist erlischt die Zeichnung automatisch.
          </p>
        </Section>

        <Section title="5. Risikohinweise">
          <p>
            Die Investition in Aktien ist mit erheblichen Risiken verbunden, einschliesslich des
            möglichen Totalverlustes des eingesetzten Kapitals. Detaillierte Informationen entnehmen
            Sie bitte unseren{' '}
            <Link to="/risikohinweise" className="text-[#C4A55A] hover:text-[#D4B56A]">
              Risikohinweisen
            </Link>.
          </p>
        </Section>

        <Section title="6. Vermittler / Provisionen">
          <p>
            Vermittler erhalten für vermittelte Investitionen eine Provision gemäss individueller
            Vermittlervereinbarung. Die Provision wird erst nach erfolgreicher Bestätigung der
            Zeichnung und Eingang der Zahlung fällig. Untervermittler dürfen keinen höheren
            Provisionssatz erhalten als ihr betreuender Hauptvermittler.
          </p>
        </Section>

        <Section title="7. Daten­schutz">
          <p>
            Die Bearbeitung personenbezogener Daten erfolgt gemäss unserer{' '}
            <Link to="/datenschutz" className="text-[#C4A55A] hover:text-[#D4B56A]">
              Datenschutzerklärung
            </Link>.
          </p>
        </Section>

        <Section title="8. Haftung">
          <p>
            Die Gesellschaft haftet nur für Schäden, die auf grobe Fahrlässigkeit oder Vorsatz
            zurückzuführen sind. Eine Haftung für entgangenen Gewinn, Folge- oder
            Mangel­folge­schäden ist im gesetzlich zulässigen Rahmen ausgeschlossen.
          </p>
        </Section>

        <Section title="9. Änderungen der AGB">
          <p>
            Die Gesellschaft behält sich vor, diese AGB jederzeit anzupassen. Massgeblich ist die
            jeweils zum Zeitpunkt der Zeichnung gültige Fassung.
          </p>
        </Section>

        <Section title="10. Anwendbares Recht und Gerichtsstand">
          <p>
            Es gilt ausschliesslich Schweizer Recht unter Ausschluss des UN-Kaufrechts.
            Gerichtsstand ist Schaffhausen, Schweiz.
          </p>
        </Section>

        <p className="text-gray-500 text-sm mt-10 text-center">
          Stand: {new Date().toLocaleDateString('de-CH', { day: '2-digit', month: 'long', year: 'numeric' })}
        </p>
      </main>
      <Footer />
    </div>
  );
};
